import { ApolloClient, InMemoryCache, ApolloLink } from "@apollo/client";
import { createUploadLink } from "apollo-upload-client";
import omitDeep from "omit-deep";
import i18n from "i18next";
import * as Sentry from "@sentry/react";
import { onError } from "@apollo/client/link/error";
import { SentryLink } from "apollo-link-sentry";
import { RetryLink } from "@apollo/client/link/retry";
import { authTokenExcludedOperations } from "./auth-token-excluded-operations";

/**
 * Apollo Client v2 - connects to the new NestJS GraphQL API
 * Endpoint: https://app.dev.nest.stellastays.com/graphql
 */

/**
 * Error link to report GraphQL errors to Sentry.
 */
const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  Sentry.withScope((scope) => {
    scope.setTransactionName(operation.operationName);
    scope.setContext("apolloGraphQLOperation", {
      operationName: operation.operationName,
      variables: operation.variables,
      extensions: operation.extensions,
    });

    graphQLErrors?.forEach((error) => {
      Sentry.captureMessage(error.message, {
        level: "error",
        fingerprint: ["{{ default }}", "{{ transaction }}"],
        contexts: {
          apolloGraphQLError: {
            error,
            message: error.message,
            extensions: error.extensions,
          },
        },
      });
    });

    if (networkError) {
      Sentry.captureMessage(networkError.message, {
        level: "error",
        contexts: {
          apolloNetworkError: {
            error: networkError,
            // TODO: fix typing issue
            // @ts-expect-error don't know what is the type issue for now
            extensions: networkError?.extensions,
          },
        },
      });
    }
  });
});

/**
 * Get the v2 GraphQL endpoint URI
 */
const getEndpointUri = (): string => {
  return (
    import.meta.env.VITE_GRAPHQL_V2_ENDPOINT_URI ||
    "https://app.dev.nest.stellastays.com/graphql"
  );
};

/**
 * Link options for the upload link
 */
const getOperationNameFromParsedBody = (
  body: unknown
): string | undefined => {
  if (!body || typeof body !== "object") {
    return undefined;
  }

  return "operationName" in body && typeof body.operationName === "string"
    ? body.operationName
    : undefined;
};

const getOperationNameFromRequest = (
  uri: RequestInfo | URL,
  body: RequestInit["body"]
): string | undefined => {
  if (typeof body === "string") {
    try {
      return getOperationNameFromParsedBody(JSON.parse(body));
    } catch {
      return undefined;
    }
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    const operations = body.get("operations");

    if (typeof operations === "string") {
      try {
        return getOperationNameFromParsedBody(JSON.parse(operations));
      } catch {
        return undefined;
      }
    }
  }

  try {
    const requestUrl =
      typeof uri === "string"
        ? uri
        : uri instanceof URL
          ? uri.toString()
          : uri.url;

    return new URL(requestUrl, window.location.origin).searchParams.get(
      "operationName"
    ) || undefined;
  } catch {
    return undefined;
  }
};

const linkOptions = {
  uri: getEndpointUri(),
  fetch: (uri: RequestInfo | URL, options: RequestInit) => {
    const headers = (options.headers ?? {}) as Record<string, string>;
    const token = localStorage.getItem("token");
    const operationName = getOperationNameFromRequest(uri, options.body);
    const shouldExcludeAuthToken = operationName
      ? authTokenExcludedOperations.has(operationName)
      : false;

    options.headers = headers;

    if (token?.trim() && !shouldExcludeAuthToken) {
      headers["auth-token"] = token;
    } else {
      delete headers["auth-token"];
    }
    headers["accept-language"] = i18n.languages?.[0] || "en";
    return fetch(uri, options);
  },
};

/**
 * Middleware to strip __typename from variables
 */
const apolloMiddleware = new ApolloLink((operation, forward) => {
  if (operation.variables && !operation.variables.file) {
    operation.variables = omitDeep(operation.variables, "__typename");
  }
  return forward(operation);
});

/**
 * Retry link for handling transient network errors
 */
const retryLink = new RetryLink({
  attempts: {
    max: 10,
    retryIf: (error) => !!error,
  },
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
});

// Create upload link
const uploadLink = createUploadLink(linkOptions);

/**
 * Apollo Client v2 instance
 *
 * Use this client for new features connecting to the NestJS GraphQL API.
 * Import generated hooks from './generated/graphql' for type-safe queries.
 *
 * @example
 * ```typescript
 * import { clientV2 } from 'lib/apollo-v2';
 * import { useMyQueryQuery } from 'lib/apollo-v2/generated/graphql';
 *
 * // Using with ApolloProvider
 * <ApolloProvider client={clientV2}>
 *   <App />
 * </ApolloProvider>
 *
 * // Or using generated hooks directly
 * const { data } = useMyQueryQuery();
 * ```
 */
export const clientV2 = new ApolloClient({
  uri: getEndpointUri(),
  cache: new InMemoryCache(),
  link: ApolloLink.from([
    new SentryLink() as unknown as ApolloLink,
    errorLink,
    retryLink,
    apolloMiddleware.concat(uploadLink as unknown as ApolloLink),
  ]),
});

export default clientV2;
